import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Select, 
  DatePicker, 
  Button, 
  InputNumber, 
  Table, 
  message, 
  Modal,
  Divider, 
  Space,
  Tag,
  Input,
  Alert
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, UserOutlined, ShoppingOutlined, ReloadOutlined, CarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import AccountSelectModal from '../components/AccountSelectModal';
import ProductSelectModal from '../components/ProductSelectModal';
import { api } from '../services/api';
import {
  TAX_RATE_OPTIONS,
  fetchCustomerVehicles,
  createCustomerVehicle,
  VEHICLE_ASSIGNED_TO_OTHER_CODE,
  type CustomerVehicleDto,
} from '../services/serviceOrderService';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';
import { useAuth } from '../contexts/AuthContext';
import { trUpperNormalize } from '../utils/trLocale';

interface Product {
  id: number;
  productCode: string;
  productName: string;
  unitSetId: number;
  unitSetName: string;
  baseUnitId: number;
  baseUnitName: string;
  taxRate?: number; // Ürünün KDV oranı
  variants: ProductVariant[];
  units: Unit[];
}

interface ProductVariant {
  id: number;
  sku: string;
  price: number;
  stock: number;
  variantValues: Array<{
    variantName: string;
    variantValue: string;
  }>;
}

interface Unit {
  id: number;
  name: string;
  symbol: string;
  baseRatio: number;
}

interface DocumentItem {
  id?: number;
  lineNo: number;
  productId?: number;  // Varyantı olmayan ürünler için
  productVariantId?: number;  // Varyantlı ürünler için (nullable)
  productCode: string;
  productName: string;
  variantSku: string;
  variantDescription: string;
  unitId: number;
  unitName: string;
  quantity: number;
  unitPrice: number;
  discountRate: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  baseUnitQuantity: number;
  baseUnitName: string;
  shelfCode?: string; // Raf kodu
}

interface NewItemForm {
  productId?: number;
  variantId?: number;
  unitId?: number;
  quantity: number;
  unitPrice: number;
  discountRate: number;
  taxRate: number;
}

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  accountType: string;
  customerCategory?: string; // "Retail" veya "Wholesale"
}

interface Warehouse {
  id: number;
  warehouseCode: string;
  warehouseName: string;
}

interface DocumentType {
  id: number;
  typeCode: string;
  typeName: string;
  movementType: string;
  affectsStock: boolean;
  requiresAccount: boolean;
  priceTypeId?: number; // Hangi fiyat tipini kullanacağını belirler (1: Perakende, 2: Toptan, 3: Alış)
}

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isDefault?: boolean;
}

const AddDocument: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentItems, setDocumentItems] = useState<DocumentItem[]>([]);
  const previousDocumentTypeRef = React.useRef<number | undefined>(undefined);
  const [defaultTaxRate, setDefaultTaxRate] = useState<number>(20); // Varsayılan KDV oranı
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [baseCurrencyCode, setBaseCurrencyCode] = useState<string>('TRY');
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | undefined>();
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [loadingRate, setLoadingRate] = useState<boolean>(false);
  
  // Modal states
  const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedCustomerVehicleId, setSelectedCustomerVehicleId] = useState<number | null>(null);
  const [documentVehicles, setDocumentVehicles] = useState<CustomerVehicleDto[]>([]);
  const [documentVehiclesLoading, setDocumentVehiclesLoading] = useState(false);
  const [addVehicleModalVisible, setAddVehicleModalVisible] = useState(false);
  const [addVehicleForm] = Form.useForm();
  const [addVehicleBrandId, setAddVehicleBrandId] = useState<number | undefined>();
  const [addVehicleSaving, setAddVehicleSaving] = useState(false);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  
  // Barkod scanner
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeInputRef = React.useRef<any>(null);
  
  const [newItem, setNewItem] = useState<NewItemForm>({
    productId: undefined,
    variantId: undefined,
    unitId: undefined,
    quantity: 0,
    unitPrice: 0,
    discountRate: 0,
    taxRate: 20 // Settings'ten yüklenecek
  });

  useEffect(() => {
    loadProducts();
    loadWarehouses();
    loadDocumentTypes();
    loadSettings();
    loadCurrencies();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Kullanıcının varsayılan deposunu otomatik seç (user ve warehouses yüklendikten sonra)
  useEffect(() => {
    if (user?.defaultWarehouseId && warehouses.length > 0) {
      const defaultWarehouse = warehouses.find(w => w.id === user.defaultWarehouseId);
      if (defaultWarehouse && !form.getFieldValue('warehouseId')) {
        form.setFieldsValue({ warehouseId: user.defaultWarehouseId });
      }
    }
  }, [user, warehouses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Barkod input'a otomatik focus - KALDIRILDI
  // useEffect(() => {
  //   if (barcodeInputRef.current) {
  //     barcodeInputRef.current.focus();
  //   }
  // }, [documentItems]); // Her satır eklendiğinde tekrar focus

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      setProductsError(null);
      const { data } = await api.get('/products/for-documents');
      setProducts(Array.isArray(data) ? data : []);
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Ürünler yüklenirken hata oluştu';
      setProductsError(errMsg);
      message.error(errMsg);
      setProducts([]);
      console.error('Ürünler yüklenirken hata:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const { data } = await api.get('/warehouses');
      setWarehouses(data);
    } catch (error) {
      message.error('Depolar yüklenirken hata oluştu');
      console.error('Depolar yüklenirken hata oluştu:', error);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const { data } = await api.get('/documenttypes', { params: { stockOnly: true } });
      setDocumentTypes(data);
    } catch (error) {
      message.error('Fiş tipleri yüklenirken hata oluştu');
      console.error('Fiş tipleri yüklenirken hata oluştu:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get<{ DefaultTaxRate?: string }>('/generalsettings');
      if (data?.DefaultTaxRate) {
        const raw = parseFloat(data.DefaultTaxRate);
        const taxRate = TAX_RATE_OPTIONS.includes(raw) ? raw : 20;
        setDefaultTaxRate(taxRate);
        setNewItem(prev => ({ ...prev, taxRate }));
      }
    } catch (error) {
      console.error('Settings yüklenirken hata:', error);
    }
  };

const loadCurrencies = async () => {
  try {
    const { data } = await api.get<{ baseCurrencyCode: string; items: Currency[] }>('/currencies');
    if (data?.items) {
      setCurrencies(data.items);
      const baseCode = data.baseCurrencyCode || 'TRY';
      setBaseCurrencyCode(baseCode);
      const defaultCurrency = data.items.find(c => c.isDefault) || data.items[0];
      if (defaultCurrency) {
        setSelectedCurrencyId(defaultCurrency.id);
        setExchangeRate(1);
        form.setFieldsValue({
          currencyId: defaultCurrency.id,
          exchangeRate: 1
        });
        if (defaultCurrency.code !== baseCode) {
          fetchExchangeRate(defaultCurrency.id, data.items);
        }
      }
    }
  } catch (error) {
    console.error('Para birimleri yüklenirken hata:', error);
    message.error('Para birimleri yüklenemedi');
  }
};

const selectedCurrency = useMemo(() => {
  return currencies.find(c => c.id === selectedCurrencyId);
}, [currencies, selectedCurrencyId]);

const isBaseCurrency = useMemo(() => {
  if (!selectedCurrency) return true;
  return selectedCurrency.code === baseCurrencyCode;
}, [selectedCurrency, baseCurrencyCode]);

const currencySymbol = selectedCurrency?.symbol || '₺';
const currencyCode = selectedCurrency?.code || 'TRY';

const baseCurrency = useMemo(() => {
  if (!currencies.length) return undefined;
  return currencies.find(c => c.code === baseCurrencyCode) || currencies.find(c => c.isDefault);
}, [currencies, baseCurrencyCode]);

const baseCurrencySymbol = baseCurrency?.symbol || '₺';

const effectiveExchangeRate = useMemo(() => {
  if (isBaseCurrency) return 1;
  return exchangeRate > 0 ? exchangeRate : 1;
}, [isBaseCurrency, exchangeRate]);

const totalAmountDisplay = useMemo(
  () => documentItems.reduce((sum, item) => sum + (item.lineTotal || 0), 0),
  [documentItems]
);

const totalTaxDisplay = useMemo(
  () => documentItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0),
  [documentItems]
);

const totalAmountBase = useMemo(
  () => Number((totalAmountDisplay * effectiveExchangeRate).toFixed(2)),
  [totalAmountDisplay, effectiveExchangeRate]
);

const totalTaxBase = useMemo(
  () => Number((totalTaxDisplay * effectiveExchangeRate).toFixed(2)),
  [totalTaxDisplay, effectiveExchangeRate]
);

const formatBaseMoney = (value: number) => {
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: baseCurrencyCode
    }).format(value || 0);
  } catch {
    const amount = Number.isFinite(value) ? value : 0;
    return `${baseCurrencySymbol} ${amount.toFixed(2)}`;
  }
};

const formatMoney = (value: number) => {
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode
    }).format(value || 0);
  } catch {
    const amount = Number.isFinite(value) ? value : 0;
    return `${currencySymbol} ${amount.toFixed(2)}`;
  }
};

const getPriceForSelectedCurrency = (amount: number | undefined | null) => {
  if (!amount) return 0;
  if (isBaseCurrency || !exchangeRate || exchangeRate <= 0) return amount;
  return Number((amount / exchangeRate).toFixed(4));
};

const fetchExchangeRate = async (currencyId: number, overrideList?: Currency[]) => {
  const list = overrideList ?? currencies;
  if (!list.length) return;
  const currency = list.find(c => c.id === currencyId);
  if (!currency) return;
  if (currency.code === baseCurrencyCode) {
    setExchangeRate(1);
    form.setFieldsValue({ exchangeRate: 1 });
    return;
  }
  try {
    setLoadingRate(true);
    const { data } = await api.get<{ exchangeRate: number }>(`/currencies/${currencyId}/rate`);
    if (data?.exchangeRate && data.exchangeRate > 0) {
      setExchangeRate(data.exchangeRate);
      form.setFieldsValue({ exchangeRate: data.exchangeRate });
      message.success(`${currency.code} kuru güncellendi`);
    } else {
      message.warning('Kur bilgisi alınamadı, lütfen manuel giriniz.');
    }
  } catch (error) {
    console.error('Kur bilgisi alınırken hata:', error);
    message.error('Kur bilgisi alınamadı');
  } finally {
    setLoadingRate(false);
  }
};

const handleCurrencyChange = async (value: number) => {
  if (documentItems.length > 0) {
    message.info('Para birimi değiştirildi, satırlar sıfırlandı');
    setDocumentItems([]);
  }
  setSelectedCurrencyId(value);
  setExchangeRate(1);
  form.setFieldsValue({ currencyId: value, exchangeRate: 1 });
  await fetchExchangeRate(value);
};

const handleRefreshExchangeRate = async () => {
  if (!selectedCurrencyId) return;
  await fetchExchangeRate(selectedCurrencyId);
};

const handleExchangeRateChange = (value: number | null) => {
  const safeValue = value && value > 0 ? value : 1;
  setExchangeRate(safeValue);
  form.setFieldsValue({ exchangeRate: safeValue });
};

  // Cari hesap seçildiğinde
  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setSelectedCustomerVehicleId(null);
    form.setFieldsValue({ accountId: account.id });
    message.success(`${account.accountCode} - ${account.companyName || `${account.firstName} ${account.lastName}`} seçildi`);
  };

  const documentTypeId = Form.useWatch('documentType', form);
  const showVehicleSelection = documentTypeId === 2 || documentTypeId === 6; // mal satış, stok çıkış

  const openVehicleModal = async () => {
    if (!selectedAccount) return;
    setVehicleModalVisible(true);
    setDocumentVehiclesLoading(true);
    try {
      const list = await fetchCustomerVehicles({ customerId: selectedAccount.id });
      setDocumentVehicles(list || []);
    } catch (e) {
      message.error('Araç listesi yüklenemedi');
      setDocumentVehicles([]);
    } finally {
      setDocumentVehiclesLoading(false);
    }
  };

  const selectedVehicle = useMemo(
    () => documentVehicles.find(v => v.id === selectedCustomerVehicleId),
    [documentVehicles, selectedCustomerVehicleId]
  );

  useEffect(() => {
    if (addVehicleModalVisible && brands.length === 0) {
      fetchBrands().then(setBrands).catch(() => setBrands([]));
    }
  }, [addVehicleModalVisible, brands.length]);

  useEffect(() => {
    if (addVehicleBrandId) {
      fetchModels(addVehicleBrandId).then(setModels).catch(() => setModels([]));
    } else {
      setModels([]);
      addVehicleForm.setFieldsValue({ modelId: undefined });
    }
  }, [addVehicleBrandId, addVehicleForm]);

  const openAddVehicleModal = () => {
    addVehicleForm.resetFields();
    addVehicleForm.setFieldsValue({ currentKm: 0 });
    setAddVehicleBrandId(undefined);
    setModels([]);
    setAddVehicleModalVisible(true);
  };

  const buildAddVehiclePayload = (reassign = false) => {
    const v = addVehicleForm.getFieldsValue();
    if (!selectedAccount) throw new Error('Cari seçili değil');
    const rawPlate = (v.plate != null ? String(v.plate).trim() : '');
    const plate = rawPlate !== '' ? rawPlate.toLocaleUpperCase('tr-TR') : rawPlate;
    return {
      customerId: selectedAccount.id,
      plate,
      vinChassis: v.vinChassis,
      brandId: v.brandId,
      modelId: v.modelId,
      make: v.make,
      model: v.model,
      year: v.year,
      color: v.color,
      engineNo: v.engineNo,
      currentKm: v.currentKm ?? 0,
      notes: v.notes,
      reassignToNewCustomer: reassign,
    };
  };

  const handleSaveNewVehicle = async () => {
    if (!selectedAccount) {
      message.warning('Önce cari hesap seçin');
      return;
    }
    try {
      await addVehicleForm.validateFields();
      setAddVehicleSaving(true);
      const created = await createCustomerVehicle(buildAddVehiclePayload(false));
      message.success('Araç eklendi');
      const list = await fetchCustomerVehicles({ customerId: selectedAccount.id });
      setDocumentVehicles(list || []);
      const toSelect = list?.find((v) => v.id === created.id) ?? (created as CustomerVehicleDto);
      setSelectedCustomerVehicleId(toSelect.id);
      setAddVehicleModalVisible(false);
      setVehicleModalVisible(false);
    } catch (err: any) {
      if (err.errorFields) {
        setAddVehicleSaving(false);
        return;
      }
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.code === VEHICLE_ASSIGNED_TO_OTHER_CODE) {
        const { plate, currentCustomerName, newCustomerName } = data;
        Modal.confirm({
          title: 'Araç atama onayı',
          content: `${plate} plakalı araç ${currentCustomerName} adına kayıtlıdır. ${newCustomerName} müşterisine atanacaktır. Devam edilsin mi?`,
          okText: 'Evet',
          cancelText: 'İptal',
          onOk: async () => {
            try {
              const created = await createCustomerVehicle(buildAddVehiclePayload(true));
              message.success('Araç yeni müşteriye atandı');
              const list = await fetchCustomerVehicles({ customerId: selectedAccount.id });
              setDocumentVehicles(list || []);
              const toSelect = list?.find((v) => v.id === created.id) ?? (created as CustomerVehicleDto);
              setSelectedCustomerVehicleId(toSelect.id);
              setAddVehicleModalVisible(false);
              setVehicleModalVisible(false);
            } catch (e2: any) {
              message.error(e2.response?.data?.message || 'Atama yapılamadı');
            } finally {
              setAddVehicleSaving(false);
            }
          },
          onCancel: () => setAddVehicleSaving(false),
        });
        setAddVehicleSaving(false);
        return;
      }
      message.error(data?.message || 'Araç eklenirken hata oluştu');
    } finally {
      setAddVehicleSaving(false);
    }
  };

  // Ürün modal'dan seçildiğinde - otomatik olarak satıra ekle
  const handleProductSelect = async (
    productData: any, 
    selectedUnit: Unit, 
    priceTypeId: number
  ) => {
    // Ürünü products dizisinden bul (yeni oluşturulan ürünler için bulunamayabilir)
    const product = products.find(p => p.id === productData.productId);
    
    // Eğer ürün products listesinde yoksa (yeni oluşturulmuş olabilir), productData'dan bilgileri kullan
    const baseUnitName = product?.baseUnitName || selectedUnit.name;
    
    // KDV oranını ürünün kendi değerinden al, yoksa settings'ten varsayılan değer
    const taxRate = product?.taxRate || productData.taxRate || defaultTaxRate;

    // Document type'a göre fiyatı belirle (1: Perakende, 2: Toptan, 3: Alış)
    console.log('handleProductSelect - productData:', productData);
    console.log('handleProductSelect - priceTypeId:', priceTypeId);
    console.log('handleProductSelect - retailPrice:', productData.retailPrice);
    console.log('handleProductSelect - wholesalePrice:', productData.wholesalePrice);
    console.log('handleProductSelect - purchasePrice:', productData.purchasePrice);
    
    let unitPrice: number | undefined;
    if (priceTypeId === 1) {
      unitPrice = productData.retailPrice;
    } else if (priceTypeId === 2) {
      unitPrice = productData.wholesalePrice;
    } else if (priceTypeId === 3) {
      unitPrice = productData.purchasePrice;
    }
    
    console.log('handleProductSelect - seçilen unitPrice:', unitPrice);
    console.log('handleProductSelect - taxRate:', taxRate);

    if (!unitPrice || unitPrice === 0) {
      message.warning('Bu ürünün fiyatı tanımlanmamış. Lütfen manuel olarak fiyat girin.');
    }

    // Base quantity hesapla
    const baseQuantity = 1 * selectedUnit.baseRatio;

    // Varyant değerlerini formatla
    const variantDescription = productData.variantDescription || 'Standart';

    // Varyantı olmayan ürünler için ProductId, varyantlı ürünler için ProductVariantId
    const hasVariant = productData.variantId && productData.variantId !== 0;

    // Raf kodunu otomatik doldur: Seçili warehouse'daki ProductStock'tan al
    let shelfCode: string | undefined = undefined;
    const selectedWarehouseId = form.getFieldValue('warehouseId');
    if (selectedWarehouseId) {
      try {
        const { data: stocks } = await api.get(`/stock/by-product/${productData.productId}`);
        const productStock = stocks.find((s: any) => 
          s.warehouseId === selectedWarehouseId && 
          (hasVariant ? s.productVariantId === productData.variantId : !s.productVariantId)
        );
        if (productStock?.shelfCode) {
          shelfCode = productStock.shelfCode;
        }
      } catch (error) {
        console.error('Raf kodu yüklenirken hata:', error);
      }
    }

    // Yeni satır oluştur
    const displayUnitPrice = getPriceForSelectedCurrency(unitPrice || 0);

    const newDocumentItem: DocumentItem = {
      lineNo: documentItems.length + 1,
      productId: hasVariant ? undefined : productData.productId,  // Varyantı yoksa ProductId
      productVariantId: hasVariant ? productData.variantId : undefined,  // Varyant varsa ProductVariantId
      productCode: productData.productCode,
      productName: productData.productName,
      variantSku: productData.sku || '',
      variantDescription: variantDescription,
      unitId: selectedUnit.id,
      unitName: selectedUnit.name,
      quantity: 1, // Varsayılan 1 adet
      unitPrice: unitPrice ? displayUnitPrice : 0, // Fiyat yoksa 0
      discountRate: 0,
      discountAmount: 0,
      taxRate: taxRate,
      taxAmount: ((displayUnitPrice || 0) * taxRate) / 100,
      lineTotal: (displayUnitPrice || 0) + ((displayUnitPrice || 0) * taxRate) / 100,
      baseUnitQuantity: baseQuantity,
      baseUnitName: baseUnitName,
      shelfCode: shelfCode // Raf kodu otomatik dolduruldu
    };

    setDocumentItems(prev => [...prev, newDocumentItem]);
    
    // Ürünleri yeniden yükle (yeni oluşturulan ürünü eklemek için)
    if (!product) {
      loadProducts();
    }
    
    if (!hasVariant) {
      message.success('Ürün satıra eklendi (Varyant yok)');
    } else {
      message.success('Ürün satıra eklendi');
    }
  };

  // Barkod ile ürün arama ve otomatik ekleme
  const handleBarcodeScan = async () => {
    if (!barcodeInput.trim()) return;
    
    if (!selectedAccount) {
      message.warning('Önce cari hesap seçmelisiniz!');
      setBarcodeInput('');
      return;
    }

    try {
      const { data: productData } = await api.get(`/productbarcodes/scan/${encodeURIComponent(barcodeInput)}`);
      
      // Document type'a göre fiyat seç (1: Perakende, 2: Toptan, 3: Alış)
      const documentTypeId = form.getFieldValue('documentType');
      const documentType = documentTypes.find(dt => dt.id === documentTypeId);
      const priceTypeId = documentType?.priceTypeId || (selectedAccount.customerCategory === 'Retail' ? 1 : 2);
      
      let unitPrice: number | undefined;
      if (priceTypeId === 1) {
        unitPrice = productData.retailPrice;
      } else if (priceTypeId === 2) {
        unitPrice = productData.wholesalePrice;
      } else if (priceTypeId === 3) {
        unitPrice = productData.purchasePrice;
      }

      if (!unitPrice || unitPrice === 0) {
        message.warning(`${productData.productName} - Fiyat tanımlı değil!`);
      }

      // Units dizisinden base unit'i bul
      const baseUnit = productData.units.find((u: any) => u.id === productData.baseUnitId) || productData.units[0];
      
      if (!baseUnit) {
        message.error('Ürünün birimi tanımlı değil!');
        setBarcodeInput('');
        return;
      }

      const baseQuantity = 1 * baseUnit.baseRatio;

      // KDV oranını ürünün kendi değerinden al (products listesinden), yoksa settings'ten varsayılan değer
      const product = products.find(p => p.id === productData.productId);
      const taxRate = product?.taxRate || defaultTaxRate;

      // Yeni satır oluştur
      const displayUnitPrice = getPriceForSelectedCurrency(unitPrice || 0);

      const newDocumentItem: DocumentItem = {
        lineNo: documentItems.length + 1,
        productId: productData.variantId ? undefined : productData.productId,  // Varyantı yoksa ProductId
        productVariantId: productData.variantId || undefined,  // Varyant varsa ProductVariantId
        productCode: productData.productCode,
        productName: productData.productName,
        variantSku: productData.sku || '',
        variantDescription: productData.variantDescription || '',
        unitId: baseUnit.id,
        unitName: baseUnit.name,
        quantity: 1,
        unitPrice: displayUnitPrice,
        discountRate: 0,
        discountAmount: 0,
        taxRate: taxRate,
        taxAmount: (displayUnitPrice * taxRate) / 100,
        lineTotal: displayUnitPrice + (displayUnitPrice * taxRate) / 100,
        baseUnitQuantity: baseQuantity,
        baseUnitName: productData.baseUnitName
      };

      setDocumentItems(prev => [...prev, newDocumentItem]);
      message.success(`${productData.productName} eklendi`);
      setBarcodeInput('');
      
      // Focus'u barkod input'undan kaldır
      if (barcodeInputRef.current) {
        barcodeInputRef.current.blur();
      }
      
    } catch (error) {
      console.error('Barkod scan error:', error);
      message.error('Barkod okuma hatası!');
      setBarcodeInput('');
      
      // Hata durumunda da focus'u kaldır
      if (barcodeInputRef.current) {
        barcodeInputRef.current.blur();
      }
    }
  };

  const calculateBaseQuantity = (quantity: number, unitId: number, product: Product): number => {
    const unit = product.units.find(u => u.id === unitId);
    if (!unit) return quantity;
    
    return quantity * unit.baseRatio;
  };

  const calculateLineTotal = (item: NewItemForm): number => {
    const quantity = item.quantity || 0;
    const unitPrice = item.unitPrice || 0;
    const discountRate = item.discountRate || 0;
    const taxRate = item.taxRate || 0;
    
    const subtotal = quantity * unitPrice;
    const discountAmount = (subtotal * discountRate) / 100;
    const taxAmount = ((subtotal - discountAmount) * taxRate) / 100;
    
    return subtotal - discountAmount + taxAmount;
  };

  const handleAddItem = () => {
    if (!newItem.productId || !newItem.unitId || !newItem.quantity || !newItem.unitPrice) {
      message.error('Lütfen tüm alanları doldurun');
      return;
    }

    const selectedProduct = products.find(p => p.id === newItem.productId);
    const selectedVariant = newItem.variantId 
      ? selectedProduct?.variants.find(v => v.id === newItem.variantId)
      : null;
    const selectedUnit = selectedProduct?.units.find(u => u.id === newItem.unitId);
    
    if (!selectedProduct || !selectedUnit) {
      message.error('Geçersiz ürün veya birim seçimi');
      return;
    }

    // Ürünün varyantı varsa varyant seçimi zorunlu
    if (selectedProduct.variants && selectedProduct.variants.length > 0 && !selectedVariant) {
      message.error('Bu ürün için varyant seçimi zorunludur');
      return;
    }

    const baseQuantity = calculateBaseQuantity(newItem.quantity!, newItem.unitId!, selectedProduct);
    const lineTotal = calculateLineTotal(newItem);

    const variantDescription = selectedVariant?.variantValues && selectedVariant.variantValues.length > 0
      ? selectedVariant.variantValues.map(vv => `${vv.variantName}: ${vv.variantValue}`).join(', ')
      : '';

    const newDocumentItem: DocumentItem = {
      lineNo: documentItems.length + 1,
      productId: selectedVariant ? undefined : selectedProduct.id,  // Varyantı yoksa ProductId
      productVariantId: selectedVariant ? newItem.variantId : undefined,  // Varyant varsa ProductVariantId
      productCode: selectedProduct.productCode,
      productName: selectedProduct.productName,
      variantSku: selectedVariant?.sku || '',
      variantDescription: variantDescription,
      unitId: newItem.unitId!,
      unitName: selectedUnit.name,
      quantity: newItem.quantity!,
      unitPrice: newItem.unitPrice!,
      discountRate: newItem.discountRate || 0,
      discountAmount: (newItem.quantity! * newItem.unitPrice! * (newItem.discountRate || 0)) / 100,
      taxRate: newItem.taxRate || 0,
      taxAmount: ((newItem.quantity! * newItem.unitPrice!) - ((newItem.quantity! * newItem.unitPrice! * (newItem.discountRate || 0)) / 100)) * (newItem.taxRate || 0) / 100,
      lineTotal,
      baseUnitQuantity: baseQuantity,
      baseUnitName: selectedProduct.baseUnitName
    };

    setDocumentItems(prev => [...prev, newDocumentItem]);
    setNewItem({
      productId: undefined,
      variantId: undefined,
      unitId: undefined,
      quantity: 0,
      unitPrice: 0,
      discountRate: 0,
      taxRate: defaultTaxRate
    });
    message.success('Satır eklendi');
  };

  const handleDeleteItem = (lineNo: number) => {
    setDocumentItems(prev => prev.filter(item => item.lineNo !== lineNo).map((item, index) => ({
      ...item,
      lineNo: index + 1
    })));
    message.success('Satır silindi');
  };

  // Satır güncelleme fonksiyonları
  const updateDocumentItem = (lineNo: number, field: keyof DocumentItem, value: any) => {
    setDocumentItems(prev => prev.map(item => {
      if (item.lineNo !== lineNo) return item;

      const updatedItem = { ...item, [field]: value };

      // Fiyat hesaplamaları
      const quantity = field === 'quantity' ? value : item.quantity;
      const unitPrice = field === 'unitPrice' ? value : item.unitPrice;
      const discountRate = field === 'discountRate' ? value : item.discountRate;
      const taxRate = field === 'taxRate' ? value : item.taxRate;

      const subtotal = quantity * unitPrice;
      const discountAmount = (subtotal * discountRate) / 100;
      const taxableAmount = subtotal - discountAmount;
      const taxAmount = (taxableAmount * taxRate) / 100;
      const lineTotal = taxableAmount + taxAmount;

      updatedItem.discountAmount = discountAmount;
      updatedItem.taxAmount = taxAmount;
      updatedItem.lineTotal = lineTotal;

      // Birim değişirse base quantity'yi güncelle
      if (field === 'unitId') {
        const product = products.find(p => p.id === item.productVariantId);
        if (product) {
          const unit = product.units.find(u => u.id === value);
          if (unit) {
            updatedItem.baseUnitQuantity = quantity * unit.baseRatio;
            updatedItem.unitName = unit.name;
          }
        }
      }

      return updatedItem;
    }));
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (documentItems.length === 0) {
        message.error('En az bir satır eklemelisiniz');
        return;
      }

      if (!selectedCurrencyId) {
        message.error('Para birimi seçmelisiniz');
        return;
      }

      if (!values.documentDate || !dayjs.isDayjs(values.documentDate)) {
        message.error('Lütfen geçerli bir tarih seçiniz');
        return;
      }

      const rate = effectiveExchangeRate;
      const convertToBase = (amount: number | undefined, decimals: number) =>
        Number((((amount ?? 0)) * rate).toFixed(decimals));
      const toDisplayAmount = (amount: number | undefined, decimals: number) =>
        Number((amount ?? 0).toFixed(decimals));

      const items = documentItems.map(item => {
        const unitPriceBase = convertToBase(item.unitPrice, 4);
        const discountAmountBase = convertToBase(item.discountAmount, 2);
        const taxAmountBase = convertToBase(item.taxAmount, 2);
        const lineTotalBase = convertToBase(item.lineTotal, 2);

        return {
          lineNo: item.lineNo,
          productId: item.productId || null,
          productVariantId: item.productVariantId || null,
          unitId: item.unitId,
          quantity: item.quantity,
          unitPrice: unitPriceBase,
          unitPriceEx: toDisplayAmount(item.unitPrice, 4),
          discountRate: item.discountRate,
          discountAmount: discountAmountBase,
          taxRate: item.taxRate,
          taxAmount: taxAmountBase,
          lineTotal: lineTotalBase,
          shelfCode: item.shelfCode || null // Raf kodu
        };
      });

      const totalAmountBaseCalculated = Number(items.reduce((sum, item) => sum + item.lineTotal, 0).toFixed(2));
      const totalTaxBaseCalculated = Number(items.reduce((sum, item) => sum + item.taxAmount, 0).toFixed(2));

      const documentData = (allowNegative: boolean) => ({
        documentTypeId: values.documentType,
        documentDate: (values.documentDate as Dayjs).toISOString(),
        accountId: values.accountId,
        customerVehicleId: selectedCustomerVehicleId ?? undefined,
        warehouseId: values.warehouseId,
        currencyId: selectedCurrencyId,
        exchangeRate: rate,
        totalAmount: totalAmountBaseCalculated,
        totalTax: totalTaxBaseCalculated,
        notes: values.notes,
        items,
        allowNegativeStock: allowNegative
      });

      try {
        const { data: result } = await api.post('/documents', documentData(false));
        message.success(`Fiş başarıyla oluşturuldu (${result.documentNo})`);
        navigate('/documents');
      } catch (firstError: any) {
        const res = firstError.response?.data;
        if (res?.code === 'InsufficientStock' && res?.items?.length) {
          const itemList = res.items.map((i: any) => `• ${i.productName ?? 'Ürün'}: mevcut ${i.currentStock ?? 0}, istenen ${i.requestedQuantity ?? 0}`).join('\n');
          Modal.confirm({
            title: 'Yetersiz stok',
            content: (
              <div>
                <p>{res.message ?? 'Bazı ürünlerde stok yetersiz. Negatife düşürmek istiyor musunuz?'}</p>
                <pre style={{ marginTop: 8, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{itemList}</pre>
              </div>
            ),
            okText: 'Evet, kaydet',
            cancelText: 'İptal',
            onOk: async () => {
              try {
                const { data: result } = await api.post('/documents', documentData(true));
                message.success(`Fiş başarıyla oluşturuldu (${result.documentNo})`);
                navigate('/documents');
              } catch (e: any) {
                message.error(e.response?.data?.message ?? 'Fiş kaydedilirken hata oluştu');
              }
            }
          });
        } else {
          throw firstError;
        }
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Fiş oluşturulurken bir hata oluştu');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/documents');
  };

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
          >
            Geri Dön
          </Button>
          <h1 style={{ margin: 0 }}>Yeni Fiş Oluştur</h1>
        </Space>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ documentDate: dayjs() }}
          onFinish={handleSubmit}
          onValuesChange={(changedValues, allValues) => {
            // Fiş tipi değiştiğinde eklenen ürünleri sıfırla
            if (changedValues.documentType !== undefined) {
              const currentDocumentType = changedValues.documentType;
              // Önceki değer varsa ve farklıysa sıfırla
              if (previousDocumentTypeRef.current !== undefined && 
                  previousDocumentTypeRef.current !== currentDocumentType &&
                  documentItems.length > 0) {
                setDocumentItems([]);
                message.info('Fiş tipi değiştirildi, eklenen ürünler temizlendi');
              }
              previousDocumentTypeRef.current = currentDocumentType;
            }
          }}
        >
          <Card title="Genel Bilgiler" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="documentType"
                  label="Fiş Tipi"
                  rules={[{ required: true, message: 'Fiş tipi seçiniz' }]}
                >
                  <Select placeholder="Fiş tipi seçiniz">
                    {documentTypes.map(dt => (
                      <Select.Option key={dt.id} value={dt.id}>
                        {dt.typeName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="documentDate"
                  label="Tarih"
                  rules={[{ required: true, message: 'Tarih seçiniz' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="warehouseId"
                  label="Depo"
                  rules={[{ required: true, message: 'Depo seçiniz' }]}
                >
                  <Select placeholder="Depo seçiniz">
                    {warehouses.map(warehouse => (
                      <Select.Option key={warehouse.id} value={warehouse.id}>
                        {warehouse.warehouseName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="accountId"
                  label="Cari Hesap"
                  rules={[{ required: true, message: 'Cari hesap seçiniz' }]}
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Input 
                      readOnly 
                      placeholder="Cari hesap seçin" 
                      value={selectedAccount ? 
                        `${selectedAccount.accountCode} - ${selectedAccount.companyName || `${selectedAccount.firstName} ${selectedAccount.lastName}`}` 
                        : ''
                      }
                      style={{ flex: 1 }}
                    />
                    <Button 
                      icon={<UserOutlined />} 
                      onClick={() => setIsAccountModalVisible(true)}
                    >
                      Seç
                    </Button>
                  </Space.Compact>
                </Form.Item>
                {selectedAccount?.customerCategory && (
                  <Tag color={selectedAccount.customerCategory === 'Retail' ? 'gold' : 'cyan'}>
                    {selectedAccount.customerCategory === 'Retail' ? '🏪 Perakende' : '📦 Toptan'}
                  </Tag>
                )}
              </Col>
            </Row>
            {showVehicleSelection && selectedAccount && (
              <Row gutter={16} style={{ marginTop: 0 }}>
                <Col span={12}>
                  <Form.Item label="Araç (opsiyonel)">
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        readOnly
                        placeholder="Araç seçmek için butona tıklayın"
                        value={selectedVehicle
                          ? `${selectedVehicle.plate}${[selectedVehicle.brandName || selectedVehicle.make, selectedVehicle.modelName || selectedVehicle.model].filter(Boolean).join(' ') ? ` - ${[selectedVehicle.brandName || selectedVehicle.make, selectedVehicle.modelName || selectedVehicle.model].filter(Boolean).join(' ')}` : ''}`
                          : ''}
                        style={{ flex: 1 }}
                      />
                      <Button onClick={openVehicleModal} disabled={!selectedAccount}>
                        Araç Seç
                      </Button>
                      {selectedCustomerVehicleId != null && (
                        <Button onClick={() => setSelectedCustomerVehicleId(null)}>Temizle</Button>
                      )}
                    </Space.Compact>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="currencyId"
                  label="Para Birimi"
                  rules={[{ required: true, message: 'Para birimi seçiniz' }]}
                >
                  <Select
                    placeholder="Para birimi seçiniz"
                    loading={!currencies.length}
                    onChange={handleCurrencyChange}
                  >
                    {currencies.map(currency => (
                      <Select.Option key={currency.id} value={currency.id}>
                        {currency.name} ({currency.code})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="exchangeRate"
                  label="Kur"
                  rules={[{ required: true, message: 'Kur bilgisi giriniz' }]}
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      min={0.0001}
                      step={0.0001}
                      precision={4}
                      value={exchangeRate}
                      onChange={handleExchangeRateChange}
                      style={{ width: '100%' }}
                      disabled={isBaseCurrency}
                    />
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRefreshExchangeRate}
                      loading={loadingRate}
                      disabled={isBaseCurrency}
                    />
                  </Space.Compact>
                </Form.Item>
                {!isBaseCurrency && (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    1 {currencyCode} ≈ {exchangeRate} {baseCurrencyCode}
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <Divider>Fiş Satırları</Divider>

          {/* Eski manuel ekleme Card'ı - artık kullanılmıyor */}
          <Card 
            title="Yeni Satır Ekle" 
            style={{ 
              marginBottom: 16,
              display: 'none'  // Gizlendi - Barkod ve modal ile ekleme yapılıyor
            }}
          >
            <Row gutter={16} align="bottom" style={{ display: 'none' }}>
              <Col span={4}>
                <label>Ürün</label>
                <Select
                  placeholder="Ürün seçiniz"
                  value={newItem.productId}
                  onChange={(value) => {
                    setNewItem(prev => ({
                      ...prev,
                      productId: value,
                      variantId: undefined,
                      unitId: undefined
                    }));
                  }}
                  style={{ width: '100%' }}
                >
                  {products.map(product => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.productCode} - {product.productName}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={3}>
                <label>Varyant</label>
                <Select
                  placeholder="Varyant"
                  value={newItem.variantId}
                  onChange={(value) => setNewItem(prev => ({ ...prev, variantId: value }))}
                  style={{ width: '100%' }}
                  disabled={!newItem.productId}
                >
                  {newItem.productId && 
                    products.find(p => p.id === newItem.productId)?.variants.map(variant => (
                      <Select.Option key={variant.id} value={variant.id}>
                        {variant.sku}
                        {variant.variantValues.length > 0 && ` (${variant.variantValues.map(vv => vv.variantValue).join(', ')})`}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Col>
              <Col span={3}>
                <label>Birim</label>
                <Select
                  placeholder="Birim"
                  value={newItem.unitId}
                  onChange={(value) => setNewItem(prev => ({ ...prev, unitId: value }))}
                  style={{ width: '100%' }}
                  disabled={!newItem.productId}
                >
                  {newItem.productId && 
                    products.find(p => p.id === newItem.productId)?.units.map(unit => (
                      <Select.Option key={unit.id} value={unit.id}>
                        {unit.name} ({unit.symbol})
                      </Select.Option>
                    ))
                  }
                </Select>
              </Col>
              <Col span={2}>
                <label>Miktar</label>
                <InputNumber
                  placeholder="0"
                  value={newItem.quantity}
                  onChange={(value) => setNewItem(prev => ({ ...prev, quantity: value || 0 }))}
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Col>
              <Col span={3}>
                <label>Birim Fiyat</label>
                <InputNumber
                  placeholder="0.00"
                  value={newItem.unitPrice}
                  onChange={(value) => setNewItem(prev => ({ ...prev, unitPrice: value || 0 }))}
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Col>
              <Col span={2}>
                <label>İndirim %</label>
                <InputNumber
                  placeholder="0"
                  value={newItem.discountRate}
                  onChange={(value) => setNewItem(prev => ({ ...prev, discountRate: value || 0 }))}
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                />
              </Col>
              <Col span={2}>
                <label>KDV %</label>
                <Select
                  value={newItem.taxRate ?? 20}
                  onChange={(value) => setNewItem(prev => ({ ...prev, taxRate: value ?? 20 }))}
                  style={{ width: '100%' }}
                  options={TAX_RATE_OPTIONS.map(r => ({ value: r, label: `%${r}` }))}
                />
              </Col>
              <Col span={3}>
                <label>Toplam</label>
                <div style={{ 
                  padding: '4px 11px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  backgroundColor: '#fafafa',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formatMoney(calculateLineTotal(newItem))}
                </div>
              </Col>
              <Col span={2}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddItem}
                  style={{ width: '100%' }}
                >
                  Ekle
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Document Items Table */}
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span>Eklenen Satırlar</span>
                  <Input
                    ref={barcodeInputRef}
                    placeholder="Barkod No"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onPressEnter={handleBarcodeScan}
                    style={{ width: '250px' }}
                    prefix={<span style={{ fontSize: '18px' }}>📷</span>}
                    disabled={!selectedAccount}
                    allowClear
                  />
                </div>
                <Button
                  type="dashed"
                  icon={<ShoppingOutlined />}
                  onClick={() => setIsProductModalVisible(true)}
                  disabled={!selectedAccount}
                  size="middle"
                >
                  Ürün Seç ve Ekle
                </Button>
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {productsError && (
              <Alert
                type="error"
                showIcon
                message="Ürün listesi yüklenemedi"
                description={productsError}
                action={
                  <Button size="small" onClick={() => loadProducts()}>
                    Tekrar dene
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}
            <Table
              dataSource={documentItems}
              columns={[
                {
                  title: 'Satır',
                  dataIndex: 'lineNo',
                  key: 'lineNo',
                  width: 60,
                },
                {
                  title: 'Ürün',
                  key: 'product',
                  width: 200,
                  render: (_, record) => (
                    <div>
                      <div><strong>{record.productCode}</strong></div>
                      <div style={{ fontSize: '12px' }}>{record.productName}</div>
                      {record.variantSku && (
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          SKU: {record.variantSku}
                        </div>
                      )}
                      {record.variantDescription && (
                        <div style={{ fontSize: '10px', color: '#999' }}>
                          {record.variantDescription}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Birim',
                  key: 'unitId',
                  width: 120,
                  render: (_, record) => {
                    const product = products.find(p => 
                      p.variants?.some(v => v.id === record.productVariantId) || p.id === record.productVariantId
                    );
                    
                    const availableUnits = product?.units || [];
                    
                    // Tek birim varsa direkt göster
                    if (availableUnits.length <= 1) {
                      return <span style={{ fontWeight: 500 }}>{record.unitName || 'Adet'}</span>;
                    }
                    
                    // Birden fazla birim varsa Select göster
                    return (
                      <Select
                        key={`unitId-${record.lineNo}`}
                        value={record.unitId}
                        onChange={(value) => updateDocumentItem(record.lineNo, 'unitId', value)}
                        style={{ width: '100%' }}
                        size="small"
                      >
                        {availableUnits.map(unit => (
                          <Select.Option key={unit.id} value={unit.id}>
                            {unit.name}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  },
                },
                {
                  title: 'Miktar',
                  key: 'quantity',
                  width: 100,
                  render: (_, record) => (
                    <InputNumber
                      key={`quantity-${record.lineNo}`}
                      value={record.quantity}
                      onChange={(value) => updateDocumentItem(record.lineNo, 'quantity', value || 0)}
                      style={{ width: '100%' }}
                      size="small"
                      min={0}
                      step={0.01}
                      precision={2}
                    />
                  ),
                },
                {
                  title: 'Raf Kodu',
                  key: 'shelfCode',
                  width: 120,
                  render: (_, record) => (
                    <Input
                      key={`shelfCode-${record.lineNo}`}
                      value={record.shelfCode || ''}
                      onChange={(e) => updateDocumentItem(record.lineNo, 'shelfCode', e.target.value)}
                      placeholder="Örn: A-12-3"
                      style={{ width: '100%' }}
                      size="small"
                    />
                  ),
                },
                {
                  title: 'Temel Birim',
                  key: 'baseUnit',
                  width: 120,
                  render: (_, record) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{record.baseUnitQuantity.toFixed(2)}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{record.baseUnitName}</div>
                    </div>
                  ),
                },
                {
                  title: 'Birim Fiyat',
                  key: 'unitPrice',
                  width: 120,
                  render: (_, record) => (
                  <InputNumber
                    key={`unitPrice-${record.lineNo}`}
                    value={record.unitPrice}
                    onChange={(value) => updateDocumentItem(record.lineNo, 'unitPrice', value || 0)}
                    style={{ width: '100%' }}
                    size="small"
                    min={0}
                    step={0.01}
                    precision={2}
                    formatter={value => `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => {
                      const cleaned = value?.replace(/[^\d.,-]/g, '')?.replace(/,/g, '');
                      return cleaned ? Number(cleaned) : 0;
                    }}
                  />
                  ),
                },
                {
                  title: 'İndirim %',
                  key: 'discountRate',
                  width: 100,
                  render: (_, record) => (
                    <InputNumber
                      key={`discountRate-${record.lineNo}`}
                      value={record.discountRate}
                      onChange={(value) => updateDocumentItem(record.lineNo, 'discountRate', value || 0)}
                      style={{ width: '100%' }}
                      size="small"
                      min={0}
                      max={100}
                      step={0.01}
                      precision={2}
                      formatter={value => `${value}%`}
                      parser={value => Number(value!.replace('%', ''))}
                    />
                  ),
                },
                {
                  title: 'KDV %',
                  key: 'taxRate',
                  width: 100,
                  render: (_, record) => (
                    <Select
                      key={`taxRate-${record.lineNo}`}
                      value={record.taxRate ?? 20}
                      onChange={(value) => updateDocumentItem(record.lineNo, 'taxRate', value ?? 20)}
                      style={{ width: '100%' }}
                      size="small"
                      options={TAX_RATE_OPTIONS.map(r => ({ value: r, label: `%${r}` }))}
                    />
                  ),
                },
                {
                  title: 'Toplam',
                  dataIndex: 'lineTotal',
                  key: 'lineTotal',
                  width: 100,
                  render: (total) => (
                    <strong>{formatMoney(total)}</strong>
                  ),
                },
                {
                  title: 'İşlemler',
                  key: 'actions',
                  width: 80,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteItem(record.lineNo)}
                    />
                  ),
                },
              ]}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>

          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <strong style={{ fontSize: '18px' }}>
              Toplam Tutar: {formatMoney(totalAmountDisplay)}
            </strong>
            {!isBaseCurrency && (
              <div style={{ fontSize: 12, color: '#888' }}>
                ≈ {formatBaseMoney(totalAmountBase)} ({baseCurrencyCode})
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right', marginBottom: 24 }}>
            <span style={{ fontSize: 13, color: '#888' }}>
              KDV Toplamı: {formatMoney(totalTaxDisplay)}
            </span>
            {!isBaseCurrency && (
              <div style={{ fontSize: 12, color: '#888' }}>
                ≈ {formatBaseMoney(totalTaxBase)} ({baseCurrencyCode})
              </div>
            )}
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                Fişi Kaydet
              </Button>
              <Button onClick={handleCancel}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Account Select Modal */}
      <AccountSelectModal
        visible={isAccountModalVisible}
        onClose={() => setIsAccountModalVisible(false)}
        onSelect={handleAccountSelect}
      />

      {/* Product Select Modal */}
      <ProductSelectModal
        visible={isProductModalVisible}
        onClose={() => setIsProductModalVisible(false)}
        onSelect={handleProductSelect}
        customerCategory={selectedAccount?.customerCategory}
        defaultTaxRate={defaultTaxRate}
        priceTypeId={(() => {
          const documentTypeId = form.getFieldValue('documentType');
          const documentType = documentTypes.find(dt => dt.id === documentTypeId);
          return documentType?.priceTypeId || (selectedAccount?.customerCategory === 'Retail' ? 1 : 2);
        })()}
        addedItems={documentItems.map(item => ({
          productId: item.productId ?? null,
          variantId: item.productVariantId ?? null,
        }))}
      />

      {/* Araç seçimi modalı - mal satış / stok çıkış için */}
      <Modal
        title="Araç Seç (opsiyonel)"
        open={vehicleModalVisible}
        onCancel={() => setVehicleModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button
            type="primary"
            onClick={openAddVehicleModal}
            disabled={!selectedAccount}
          >
            Yeni Araç Ekle
          </Button>
        </div>
        {documentVehiclesLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Yükleniyor...</div>
        ) : documentVehicles.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
            Bu caride kayıtlı araç bulunamadı. Yeni araç eklemek için &quot;Yeni Araç Ekle&quot; butonunu kullanın.
          </div>
        ) : (
          <Table<CustomerVehicleDto>
            dataSource={documentVehicles}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: 'Plaka', dataIndex: 'plate', key: 'plate', width: 120, render: (t: string) => <strong>{t}</strong> },
              {
                title: 'Marka / Model',
                key: 'brandModel',
                render: (_: unknown, r: CustomerVehicleDto) =>
                  [r.brandName || r.make, r.modelName || r.model].filter(Boolean).join(' ') || '—',
              },
              {
                title: '',
                key: 'action',
                width: 80,
                render: (_: unknown, record: CustomerVehicleDto) => (
                  <Button
                    type="primary"
                    size="small"
                    icon={<CarOutlined />}
                    onClick={() => {
                      setSelectedCustomerVehicleId(record.id);
                      setVehicleModalVisible(false);
                    }}
                  >
                    Seç
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Modal>

      <Modal
        title="Yeni Araç Ekle"
        open={addVehicleModalVisible}
        onOk={handleSaveNewVehicle}
        onCancel={() => {
          setAddVehicleModalVisible(false);
          addVehicleForm.resetFields();
          setAddVehicleBrandId(undefined);
          setModels([]);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={600}
        confirmLoading={addVehicleSaving}
      >
        <Form form={addVehicleForm} layout="vertical">
          <Form.Item
            name="plate"
            label="Plaka"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Lütfen plaka girin' }]}
          >
            <Input placeholder="Örn: 34ABC123" />
          </Form.Item>
          <Form.Item name="vinChassis" label="VIN/Şasi No">
            <Input placeholder="VIN veya şasi numarası" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="brandId" label="Marka">
                <Select
                  placeholder="Marka seçin (opsiyonel)"
                  showSearch
                  allowClear
                  onChange={(value) => {
                    setAddVehicleBrandId(value);
                    addVehicleForm.setFieldsValue({ modelId: undefined });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={brands.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="modelId" label="Model">
                <Select
                  placeholder="Model seçin (opsiyonel)"
                  showSearch
                  allowClear
                  disabled={!addVehicleBrandId}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={models.map((m) => ({ value: m.id, label: m.name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="make" label="Marka (Manuel)">
                <Input placeholder="Marka adı (marka seçilmediyse)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="Model (Manuel)">
                <Input placeholder="Model adı (model seçilmediyse)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="year" label="Yıl">
                <InputNumber style={{ width: '100%' }} min={1900} max={new Date().getFullYear() + 1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color" label="Renk">
                <Input placeholder="Renk" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currentKm" label="Mevcut Km">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="engineNo" label="Motor No">
            <Input placeholder="Motor numarası" />
          </Form.Item>
          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={3} placeholder="Ek notlar" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddDocument;
